import { useT } from "@/i18n";
import { framesChartMessages } from "@/i18n/messages/frames-chart";
import { cn } from "@/lib/cn";
import { ChartEndLabels } from "./chart-parts";
import { formatChartValue, type ChartDrawingProps } from "./chart-points";

const BAR_CLASS = "min-h-[3px] rounded-t-[5px] rounded-b-[2px]";
const BAR_SOLID = "bg-accent";
const BAR_SOFT = "bg-[color-mix(in_srgb,var(--color-accent)_28%,transparent)]";

/**
 * Points as bars (mockup `.bars`), the shown one solid in the accent. On the
 * card, the first and last labels follow; opened, every bar is a button with
 * its label.
 */
export function ChartBars({
  title,
  unit,
  points,
  shown,
  selected,
  onSelect,
  opened,
  testIdPrefix,
}: ChartDrawingProps) {
  const t = useT(framesChartMessages);
  const top = Math.max(...points.map((p) => Math.abs(p.value)), 0);
  const barHeight = (value: number) =>
    `${top ? (Math.abs(value) / top) * 100 : 0}%`;
  if (opened) {
    return (
      <div
        className="mt-auto flex min-h-[4em] flex-1 items-stretch gap-[0.45em] pt-[0.9em]"
        role="group"
        aria-label={t("barTrend", { title: title || t("chart") })}
        data-testid={`${testIdPrefix}-bars`}
      >
        {points.map((p, index) => (
          <button
            key={`${p.label}-${index}`}
            type="button"
            className="nodrag flex min-w-0 flex-1 flex-col justify-end gap-[0.3em]"
            aria-label={t("pointValue", {
              label: p.label,
              value: `${formatChartValue(p.value)}${unit ? ` ${unit}` : ""}`,
            })}
            aria-pressed={index === shown}
            onClick={() => onSelect(index === selected ? null : index)}
            data-testid={`${testIdPrefix}-bar-${index}`}
          >
            <span className="relative min-h-0 w-full flex-1">
              <span
                className={cn(
                  BAR_CLASS,
                  "absolute inset-x-0 bottom-0",
                  index === shown ? BAR_SOLID : BAR_SOFT,
                )}
                style={{ height: barHeight(p.value) }}
              />
            </span>
            <span className="w-full truncate text-center text-[0.78em] text-card-muted">
              {p.label}
            </span>
          </button>
        ))}
      </div>
    );
  }
  return (
    <>
      <div
        className="mt-auto flex min-h-0 flex-1 items-end gap-[0.45em] pt-[0.9em]"
        aria-hidden="true"
        data-testid={`${testIdPrefix}-bars`}
      >
        {points.map((p, index) => (
          <span
            key={index}
            className={cn(
              BAR_CLASS,
              "flex-1",
              index === shown ? BAR_SOLID : BAR_SOFT,
            )}
            style={{ height: barHeight(p.value) }}
          />
        ))}
      </div>
      <ChartEndLabels
        first={points[0]?.label ?? ""}
        last={points[shown]?.label ?? ""}
      />
    </>
  );
}
